import React from 'react';
import { CHECKOUT_MESSAGES } from '../../constants/checkout';

export interface CheckoutGuestIdentityProps {
  checkoutEmail: string;
  checkoutConfirmEmail: string;
  checkoutPassword: string;
  checkoutConfirmPassword: string;
  isEmailConfirmationRequired: boolean;
  canContinue: boolean;
  isResolvingIdentity: boolean;
  isCheckingCheckoutEmail: boolean;
  isIdentityResolved?: boolean;
  checkoutEmailExists?: boolean | null;
  onEmailChange: (email: string) => void;
  onConfirmEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (password: string) => void;
  onContinue: () => void;
}

/** Identificação de visitante no checkout: e-mail, confirmação e senha obrigatória para conta nova. */
const CheckoutGuestIdentity: React.FC<CheckoutGuestIdentityProps> = ({
  checkoutEmail,
  checkoutConfirmEmail,
  checkoutPassword,
  checkoutConfirmPassword,
  isEmailConfirmationRequired,
  canContinue,
  isResolvingIdentity,
  isCheckingCheckoutEmail,
  isIdentityResolved = false,
  checkoutEmailExists = null,
  onEmailChange,
  onConfirmEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onContinue,
}) => {
  const { identity } = CHECKOUT_MESSAGES;

  return (
    <div className="rounded-2xl border border-[#D8D1F0] bg-gradient-to-br from-[#F7F4FF] to-white p-5 sm:p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">{identity.title}</h2>
      <p className="text-sm text-gray-600">{identity.description}</p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{identity.emailLabel}</label>
        <input
          type="email"
          value={checkoutEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={identity.emailPlaceholder}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
        />
      </div>

      {checkoutEmailExists === true && !isIdentityResolved && (
        <p className="text-sm text-[#5C528A]">{identity.existingUserReady}</p>
      )}

      {isIdentityResolved && checkoutEmail.trim().length > 0 && (
        <p className="text-sm font-medium text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Email vinculado: {checkoutEmail.trim()}
        </p>
      )}

      {isEmailConfirmationRequired && (
        <>
          <p className="text-sm text-[#5C528A]">{identity.newUserPrompt}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{identity.confirmEmailLabel}</label>
            <input
              type="email"
              value={checkoutConfirmEmail}
              onChange={(e) => onConfirmEmailChange(e.target.value)}
              placeholder={identity.confirmEmailPlaceholder}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <p className="text-sm text-gray-600">{identity.passwordHint}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{identity.passwordLabel}</label>
              <input
                type="password"
                required
                minLength={6}
                value={checkoutPassword}
                onChange={(e) => onPasswordChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{identity.confirmPasswordLabel}</label>
              <input
                type="password"
                required
                minLength={6}
                value={checkoutConfirmPassword}
                onChange={(e) => onConfirmPasswordChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5C528A] focus:border-transparent"
              />
            </div>
          </div>
        </>
      )}

      <button
        type="button"
        onClick={onContinue}
        disabled={isResolvingIdentity || isCheckingCheckoutEmail || !canContinue}
        className="w-full sm:w-auto bg-[#5C528A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4A4070] transition-colors disabled:opacity-60 disabled:cursor-wait"
      >
        {isResolvingIdentity
          ? identity.validatingButton
          : isCheckingCheckoutEmail
            ? identity.checkingEmailButton
            : identity.continueButton}
      </button>
    </div>
  );
};

export default CheckoutGuestIdentity;
